import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AddressSelectionDialogComponent } from '../dialogs/address-selection-dialog/address-selection-dialog.component';
import { CopyLinesDialogComponent } from '../dialogs/copy-lines-dialog/copy-lines-dialog.component';
import { EntityBreakDialogComponent } from '../dialogs/entity-break-dialog/entity-break-dialog.component';
import { EntityCancelDialogComponent } from '../dialogs/entity-cancel-dialog/entity-cancel-dialog.component';
import { LeadTimeDialogComponent } from '../dialogs/lead-time-dialog/lead-time-dialog.component';
import { LineSummaryDialogComponent } from '../dialogs/line-summary-dialog/line-summary-dialog.component';
import { RiserChartDialogComponent } from '../dialogs/riser-chart-dialog/riser-chart-dialog.component';
import { RiserDialogComponent } from '../dialogs/riser-dialog/riser-dialog.component';
import { SalesRepDialogComponent } from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import { WarrantyTextDialogComponent } from '../dialogs/warranty-text-dialog/warranty-text-dialog.component';
import { SkyportContactDialogComponent } from '../dialogs/skyport-contact-dialog/skyport-contact-dialog.component';
import { Entity, EntityKey } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { PersonLogin, RoleName } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { EntityLineService } from '../services/entity-line-service';
import { EntityService } from '../services/entity-service';
import { OrganizationService } from '../services/organization-service';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { QuoteService } from '../services/quote-service';
import { SelectionService } from '../services/selection-service';
import { DateUtils } from '../utils/date-utils';
import { StringUtils } from '../utils/string-utils';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EntityComplexity } from '../model/entity-complexity';
import { EntityComplexityService } from '../services/entity-complexity.service';
import { ExtendedWarrantyUtils } from '../utils/extended-warranty-utils';
import { EntityLineUtils } from '../utils/entity-line-utils';
import { QewlDialogComponent } from '../dialogs/qewl-dialog/qewl-dialog.component';
import { SelectParentEntityDialogComponent } from '../dialogs/select-parent-entity-dialog/select-parent-entity-dialog.component';
import { DialogService } from '../services/dialog.service';
import { OpportunityService } from '../services/opportunity-service';
import { Opportunity } from '../opportunity/opportunity';
import { RequireOpportunityUpdatesDialogComponent } from '../dialogs/require-opp-updates-dialog/require-opp-updates-dialog.component';
import { EnterContactDialogComponent } from '../dialogs/enter-contact-dialog/enter-contact-dialog.component';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { CurrencyPipe, DatePipe, DecimalPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrandID } from '../enums/brand-id';
import { OrgunitXProjectProductType } from '../organization/organization-brands/orgunit-x-projectproducttype';
import { OrganizationProjectProductTypeService } from '../services/organization-projectproducttype-service';
import { ProjectProductType } from '../enums/project-product-type';
import { EntityUtils } from '../utils/entity-utils';
import { FileUtils } from '../utils/file-utils';
import { EntitySaveResponse } from '../entity/entity-save-response';
import { HttpErrorResponse } from '@angular/common/http';
import { LeadTimeService } from '../services/lead-time.service';
import { firstValueFrom } from 'rxjs';
import { IecLeadTime } from '../model/iec-lead-time';
import { EntityKeyUtils } from '../utils/entity-key-utils';
import { FpaService } from '../services/fpa-service';
import { WarrantyLineSummaryDialogComponent } from '../dialogs/warranty-line-summary-dialog/warranty-line-summary-dialog.component';
import { LeadTimeUtils } from '../utils/lead-time-utils';
import { Person } from '../person/person';
import { IecTaskService } from '../services/iec-task-service';
import { ShippingChargeService } from '../services/shipping-charge.service';
import { MatFormField, MatHint } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';
import { Organization } from '../organization/organization';

@Component({
  selector: 'app-quote-detail',
  templateUrl: './quote-detail.component.html',
  styleUrls: ['./quote-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatIcon,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RouterLink,
    MatProgressSpinner,
    NgClass,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
    MatFormField,
    MatHint,
    MatInput,
  ],
})
export class QuoteDetailComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly selectionService = inject(SelectionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly quoteService = inject(QuoteService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly entityLineService = inject(EntityLineService);
  private readonly personService = inject(PersonService);
  private readonly entityComplexityService = inject(EntityComplexityService);
  private readonly dialogService = inject(DialogService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly organizationProjectProductTypeService = inject(OrganizationProjectProductTypeService);
  private readonly leadTimeService = inject(LeadTimeService);
  private readonly fpaService = inject(FpaService);
  private readonly shippingChargeService: ShippingChargeService = inject(ShippingChargeService);
  private readonly taskService = inject(IecTaskService);
  readonly orgUnitId = input<string>(undefined!);
  readonly quoteId = input.required<string>({ alias: 'quote-id' });
  readonly quoteRevision = input.required<string>({ alias: 'quote-revision' });
  private personLogin!: PersonLogin;
  private readonly canCancelFpaReview = signal(false);
  private readonly canSubmitFpa = signal(false);
  readonly isRepUser = signal<boolean>(undefined!);
  controllingOrgs!: SelectionItem[];
  readonly selectedSellingOrg = signal<Organization>(undefined!);
  readonly quote = signal<Entity>(undefined!);
  readonly associatedOrders = signal<EntityKey[]>(undefined!);
  readonly isBusy = signal<boolean>(undefined!);
  readonly isNew = signal<boolean>(undefined!);
  readonly customers = signal<SelectionItem[]>(undefined!);
  readonly repOffices = signal<SelectionItem[]>(undefined!);
  readonly releaseTypes = signal<SelectionItem[]>(undefined!);
  readonly fobs = signal<SelectionItem[]>(undefined!);
  readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  readonly salesReps = signal<SelectionItem[]>(undefined!);
  readonly outsideSalesReps = signal<SelectionItem[]>(undefined!);
  readonly iecContacts = signal<SelectionItem[]>(undefined!);
  readonly leadTimes = signal<SelectionItem[]>(undefined!);
  readonly salesChannels = signal<SelectionItem[]>(undefined!);
  readonly useTypes = signal<SelectionItem[]>(undefined!);
  readonly sAndHChargeTypes = signal<SelectionItem[]>(undefined!);
  readonly shipVias = signal<SelectionItem[]>(undefined!);
  readonly deliveryCountries = signal<SelectionItem[]>(undefined!);
  readonly marketSegments = signal<SelectionItem[]>(undefined!);
  readonly projectTypes = signal<SelectionItem[]>(undefined!);
  readonly specTypes = signal<SelectionItem[]>(undefined!);
  readonly orgunitPPTs = signal<SelectionItem[]>([]);
  readonly applicationTypes = signal<SelectionItem[]>([]);
  readonly saveMessages = signal<string[]>(undefined!);
  readonly selectedLines = signal<EntityLineHeader[]>([]);
  selectionStartIndex = -1;
  readonly marketSegmentMissing = signal(false);
  readonly projectTypeMissing = signal(false);
  readonly specTypeMissing = signal(false);
  readonly applicationTypeMissing = signal(false);
  readonly skyportContactMissing = signal(false);
  readonly taggingRoomMissing = signal(false);
  readonly createdBy = signal<string>(undefined!);
  readonly updatedBy = signal<string>(undefined!);
  readonly isSiblingOrgUser = signal(false);
  readonly waitMessage = signal('Please Wait...');
  readonly hasOpenCreditDocumentsTask = signal(false);
  readonly entityComplexity = signal<EntityComplexity>(undefined!);
  readonly showAssignNewOpportunityOption = signal(false);
  readonly associatedOpportunity = signal<Opportunity>(undefined!);
  readonly orgunitXProjectProductTypes = signal<OrgunitXProjectProductType[]>(undefined!);
  readonly selectedBrand = signal<string>(undefined!);
  isRequestedShipDateValid!: boolean;
  MAX_QUICK_SHIP_QTY = 4;
  MR_MINIMUM_QTY = 100;
  readonly errorMessages = signal<string[]>([]);
  minRequestedShipDate: Date = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
    return d;
  })();
  protected readonly FileUtils = FileUtils;
  protected readonly EntityUtils = EntityUtils;
  protected readonly ProjectProductType = ProjectProductType;
  readonly isShipToStateNeeded = signal(false);
  readonly isShippingChargeTaskOpen = signal(false);
  readonly isShippingChargeTaskNeeded = signal(false);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      const primaryOrgUnit = this.personLogin.person.primaryOrgUnitId;
      if (primaryOrgUnit && primaryOrgUnit.length > 0) {
        this.organizationService.getOrganization(primaryOrgUnit).subscribe((organization) => {
          this.isSiblingOrgUser.set(this.organizationService.isSiblingOrg(organization));
          this.isRepUser.set(organization.typeCode == 'IREP' || organization.typeCode == 'OREP');
          this.initialize();
        });
      } else {
        this.isRepUser.set(false);
        this.initialize();
      }
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessages.update(current => [...current, StringUtils.getErrorMessage(error)]);
    this.waitMessage.set('Please Wait...');
  }

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    this.initializeFromRouteParams();
  }

  private loadQuoteDetails(quote: Entity) {
    this.waitMessage.set('Please Wait...');
    this.quote.set(quote);
    this.updateShippingChargeFlags();
    this.updateFpaStatusFlags();
    this.quoteService.getAssociatedOrders(quote.id.entityId, quote.id.entityRevision).subscribe((associated) => (this.associatedOrders.set(associated)));
    this.personService.getByUserLogin(this.quote().createUser).subscribe((result) => {
      if (result) {
        this.createdBy.set(result.description);
      } else {
        this.createdBy.set(this.quote().createUser);
      }
    });
    this.entityComplexityService.getEntityComplexity(quote.id).subscribe((complexity) => (this.entityComplexity.set(complexity)));
    this.loadUpdateUser();
    this.loadSellingOrganizationSelections();
    this.updateOpportunityIfRequired();
    this.checkForDiscontinuedLines();
    this.checkForOpenCreditDocumentsTask();
    this.isAssignNewOpportunityOptionDisplayed();
    this.loadLeadTimes(quote.brandID, quote.projectProductType);
    this.selectedBrand.set(EntityUtils.createBrandSelectionKey(quote.brandID, quote.projectProductType));
    this.brandChange();
  }

  private updateShippingChargeFlags(): void {
    if (!this.quote()) {
      this.isShipToStateNeeded.set(false);
      this.isShippingChargeTaskOpen.set(false);
      this.isShippingChargeTaskNeeded.set(false);
    } else if (this.isShippingNeeded()) {
      this.isShipToStateNeeded.set(StringUtils.isBlank(this.quote()?.overrideShipToStateCode));
      if (!this.isShipToStateNeeded()) {
        this.shippingChargeService.isTaskOpen(this.quote().id).subscribe({
          next: (response: string) => {
            this.isShippingChargeTaskOpen.set(StringUtils.equalsIgnoreCase('true', response));
            this.isShippingChargeTaskNeeded.set(!this.isShippingChargeTaskOpen());
          },
        });
      } else {
        this.isShippingChargeTaskOpen.set(false);
        this.isShippingChargeTaskNeeded.set(false);
      }
    } else {
      this.isShipToStateNeeded.set(false);
      this.isShippingChargeTaskOpen.set(false);
      this.isShippingChargeTaskNeeded.set(false);
    }
  }

  private isShippingNeeded(): boolean {
    const shippingCharge = this.quote()?.shippingHandlingChargeAmount || 0;
    return StringUtils.equalsIgnoreCase('PPA', this.quote()?.shipAndHandChargeType) && EntityUtils.getConfiguredQuantity(this.quote()) > 0 && shippingCharge === 0;
  }

  private checkForDiscontinuedLines(): void {
    const cutoffDate = new Date(2021, 7, 1);
    const createdDate = new Date(this.quote().createTimestamp);
    if (this.quote() && createdDate.toISOString() < cutoffDate.toISOString() && this.quote().lines) {
      const discontinuedLines = this.quote().lines.filter((line) => EntityLineUtils.hasDiscontinuedProduct(line)).map((line) => line.lineId);
      if (discontinuedLines.length > 0) {
        const messages: string[] = [];
        messages.push('Line(s) ' + discontinuedLines.join(', ') + ' have discontinued products and will need to be replaced with Vertical Floor A units');
        this.dialogService.showGenericMessageDialog(messages);
      }
    }
  }

  private checkForOpenCreditDocumentsTask(): void {
    this.taskService.getActiveTasksByNameAndEntity('Rep Credit Documents', this.quote().id.entityId, this.quote().id.entityRevision).subscribe({
      next: (tasks) => (this.hasOpenCreditDocumentsTask.set((tasks?.length || 0) > 0)),
      error: () => (this.hasOpenCreditDocumentsTask.set(false)),
    });
  }

  private loadUpdateUser(): void {
    this.personService.getByUserLogin(this.quote().updateUser).subscribe({
      next: (result: Person) => {
        if (result) {
          this.updatedBy.set(result.description);
        } else {
          this.updatedBy.set(this.quote().updateUser);
        }
      },
      error: () => (this.updatedBy.set(this.quote().updateUser)),
    });
  }

  public save(): void {
    this.resetStatus();
    this.quoteService.save(this.quote()).subscribe({
      next: (result) => {
        if (this.isNew()) {
          this.router.navigate(['quotes/' + result.updated.id.entityId + '/' + result.updated.id.entityRevision]).then(() => this.ngOnInit());
        } else {
          this.quote.set(result.updated);
          this.updateShippingChargeFlags();
        }
        this.isBusy.set(false);
        this.isNew.set(false);
        this.saveMessages.set(result.messages);
        this.loadUpdateUser();
        this.checkForOpenCreditDocumentsTask();
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessages.set([]);
    this.saveMessages.set(null!);
    this.marketSegmentMissing.set(false);
    this.projectTypeMissing.set(false);
    this.specTypeMissing.set(false);
    this.skyportContactMissing.set(false);
    this.taggingRoomMissing.set(false);
    this.applicationTypeMissing.set(false);
  }

  backToQuoteMaster(): void {
    this.router.navigate(['quotes']);
  }

  private startNewQuote(orgUnitId: string): void {
    this.isNew.set(true);
    this.quote.set(new Entity());
    this.quote().id = new EntityKey();
    this.quote().id.entityName = 'QUOTE';
    this.quote().updateAllowed = true;
    const today = DateUtils.formatOutput(new Date());
    const nullDate = DateUtils.formatOutput(new Date(1753, 0, 1));
    this.quote().createTimestamp = today;
    this.quote().createUser = this.personLogin.person.userLogin;
    this.createdBy.set(this.personLogin.person.description);
    const expirationDate = DateUtils.formatOutput(new Date());
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    this.quote().expirationDate = expirationDate;
    this.quote().releaseTypeId = 'SR';
    this.quote().priceBasisDate = today;
    this.quote().sfrPriceBasisDate = today;
    this.quote().estimatedScheduleDate = nullDate;
    this.quote().lastProcessDate = nullDate;
    this.quote().useTypeCode = 'IN';
    this.quote().advanceNoticeHours = 0;
    this.quote().deliveryCountryCode = 'USA';
    this.quote().totalList = 0;
    this.quote().netAmount = 0;
    this.quote().specialCharges = 0;
    this.quote().totalQuoteAmount = 0;
    this.quote().multiplier = 0;
    this.quote().unitQuantity = 0;
    this.quote().statusCode = 'NEW';
    this.quote().statusDescription = 'NEW';
    this.quote().approvalStatus = 'Pending Release';
    this.quote().frozen = false;
    this.quote().requestedShipDate = new Date(new Date().setDate(new Date().getDate() + 35));
    if (this.isRepUser() && !this.isSiblingOrgUser()) {
      this.quote().repOfficeId = this.personLogin.person.primaryOrgUnitId;
      this.quote().customerId = this.personLogin.person.primaryOrgUnitId;
      this.loadSellingOrganizationSelections();
    } else {
      if (orgUnitId && orgUnitId.length > 0) {
        this.quote().repOfficeId = orgUnitId;
        this.quote().customerId = orgUnitId;
        this.loadSellingOrganizationSelections();
      } else {
        this.isBusy.set(false);
      }
    }
    this.updateShippingChargeFlags();
  }

  private loadSelectionLists(): void {
    this.selectionService.getControllingOrgs().subscribe((results) => (this.repOffices.set(results)));
    this.selectionService.getOrderTypes().subscribe((results) => {
      this.releaseTypes.set(results.filter((result) => 'SR' === result.code || 'MR' === result.code));
    });
    this.selectionService.getDeliveryTerms().subscribe((results) => (this.fobs.set(results)));
    this.selectionService.getPaymentTerms().subscribe((results) => (this.paymentTerms.set(results)));
    this.selectionService.getCustomerServiceReps().subscribe((results) => (this.iecContacts.set(results)));
    this.selectionService.getFreightTerms().subscribe((results) => (this.shipVias.set(results)));
    this.selectionService.getCountryCodes().subscribe((results) => (this.deliveryCountries.set(results)));
    this.selectionService.getMarketSegments().subscribe((results) => (this.marketSegments.set(results)));
    this.selectionService.getProjectTypes().subscribe((results) => (this.projectTypes.set(results)));
    this.selectionService.getNewExtendCodes().subscribe((results) => (this.specTypes.set(results)));
    this.selectionService.getApplicationTypes().subscribe((results) => (this.applicationTypes.set(results)));
  }

  private loadLeadTimes(brandID: BrandID, projectProductType: ProjectProductType): void {
    this.leadTimes.set(null!);
    this.selectionService.getOrganizationLeadTimes(this.quote().repOfficeId, brandID, projectProductType).subscribe((results) => {
      this.leadTimes.set(results);
      this.leadTimes.set(this.leadTimes().filter((item) => item.description.toUpperCase() !== 'WARRANTY'));
      this.leadTimes.set(LeadTimeUtils.selectableLeadTimes(this.leadTimes(), this.quote().leadTimeCode));
      this.setDefaultLeadTime();
    });
  }

  private setDefaultLeadTime(): void {
    if (this.isNew()) {
      if (this.quote().brandID == 'CLM') {
        if (this.quote().projectProductType == ProjectProductType.RES) {
          this.quote().leadTimeCode = 'RES';
        } else {
          this.quote().leadTimeCode = 'COM';
        }
      }
    }
  }

  public loadSellingOrganizationSelections(): void {
    this.customers.set(null!);
    this.salesReps.set(null!);
    this.outsideSalesReps.set(null!);
    this.salesChannels.set(null!);
    this.useTypes.set(null!);
    this.sAndHChargeTypes.set(null!);
    this.selectedSellingOrg.set(null!);
    if (this.quote() && this.quote().repOfficeId) {
      this.isBusy.set(true);
      this.organizationService.getOrganization(this.quote().repOfficeId).subscribe({
        next: (sellingOrg) => {
          this.selectedSellingOrg.set(sellingOrg);
          this.selectionService.getCustomers(this.quote().repOfficeId).subscribe((results) => {
            if (results.filter((result) => result.code == this.quote().repOfficeId).length === 0) {
              const rep: SelectionItem = new SelectionItem();
              rep.code = sellingOrg.orgUnitId;
              rep.description = sellingOrg.description;
              results.unshift(rep);
            }
            this.customers.set(results);
            this.quote().customerId = this.selectFirstItemIfMissing(this.quote().customerId, results);
          });
          this.selectionService.getOrganizationChannels(this.quote().repOfficeId).subscribe((results) => {
            this.salesChannels.set(results);
            this.quote().salesChannelCode = this.selectFirstItemIfMissing(this.quote().salesChannelCode, results);
          });
          this.selectionService.getOrganizationExpediteCodes(this.quote().repOfficeId).subscribe((results) => {
            this.useTypes.set(results);
            this.quote().useTypeCode = this.selectFirstItemIfMissing(this.quote().useTypeCode, results);
          });
          this.selectionService.getOrganizationTransportationTerms(this.quote().repOfficeId).subscribe((results) => (this.sAndHChargeTypes.set(results)));
          this.organizationProjectProductTypeService.getByOrgUnitId(this.quote().repOfficeId).subscribe((result) => {
            this.orgunitXProjectProductTypes.set(result);
            for (const resultKey of this.orgunitXProjectProductTypes()) {
              const si: SelectionItem = new SelectionItem();
              si.code = EntityUtils.createBrandSelectionKey(resultKey.brandID, resultKey.projectProductType);
              si.description = EntityUtils.createBrandSelectionKey(resultKey.brandID, resultKey.projectProductType);
              this.orgunitPPTs.update(current => [...current, si]);
            }
            if (this.orgunitXProjectProductTypes() && this.orgunitXProjectProductTypes().length == 1) {
              const ppt: OrgunitXProjectProductType = this.orgunitXProjectProductTypes()[0];
              this.selectedBrand.set(EntityUtils.createBrandSelectionKey(ppt.brandID, ppt.projectProductType));
              this.brandChange();
            }
          });
          if (this.isNew()) {
            this.quote().iecContactId = sellingOrg.serviceRepId;
            this.quote().shipAndHandChargeType = sellingOrg.transportationTermsCode;
            this.quote().fobCode = sellingOrg.deliveryTermsCode;
            this.quote().paymentTermsCode = sellingOrg.paymentTermsCode;
            this.quote().shipViaCode = sellingOrg.freightTermsCode;
            this.quote().useTypeCode = sellingOrg.defaultInstallCode?.length ? sellingOrg.defaultInstallCode : 'IN';
            this.quote().salesRepId = this.isRepUser() ? this.personLogin.person.personId : sellingOrg.prnCode === 'CD' ? sellingOrg.serviceRepId : sellingOrg.responsibilityId;
          }
          this.isBusy.set(false);
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  showRepOfficeDropDown(): boolean {
    if (this.isNew()) {
      return this.isSiblingOrgUser() || !this.isRepUser();
    } else {
      return this.userHasRole('IT');
    }
  }

  private selectFirstItemIfMissing(code: string, items: SelectionItem[]): string {
    if (this.isNew()) {
      const filtered = items.filter((item) => item.code == code);
      if (!filtered || filtered.length === 0) {
        if (items && items.length > 0) {
          return items[0].code;
        } else {
          return null!;
        }
      }
    }
    return code;
  }

  public userHasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  isInternalOrRsm(): boolean {
    return this.personLoginService.isInternalOrRsm();
  }

  public userCanViewOpportunity(): boolean {
    return this.userHasRole('CustomerService') || this.userHasRole('ApplicationEngineer') || this.userHasRole('IT') || this.personLoginService.isRsm();
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setExpirationDate(newValue: Date) {
    this.quote().expirationDate = DateUtils.formatOutput(newValue);
  }

  public setSfrPriceDate(newValue: Date) {
    this.quote().sfrPriceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setPriceBasisDate(newValue: Date) {
    this.quote().priceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setRequestedShipDate(newValue: Date) {
    this.quote().requestedShipDate = DateUtils.formatOutput(newValue);
  }

  public copy(): void {
    this.isBusy.set(true);
    this.quoteService.copy(this.quote().id).subscribe({
      next: (result) => {
        this.router.navigate(['quotes', result.updated.id.entityId, result.updated.id.entityRevision]);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  public multiplier(): void {
    this.resetStatus();
    if (StringUtils.isBlank(this.quote().overrideShipToStateCode) && !LeadTimeUtils.isPartsOrWarrantyLeadTime(this.quote().leadTimeCode)) {
      this.askForStateAndRunMultiplier().then(
        () => (this.isBusy.set(false)),
        (err) => this.handleError(err),
      );
    } else {
      this.runMultiplier().then(
        () => (this.isBusy.set(false)),
        (err) => this.handleError(err),
      );
    }
  }

  private async askForStateAndRunMultiplier(): Promise<void> {
    const stateCode: string = await firstValueFrom<string>(this.dialogService.showStateSelectionDialog());
    if (StringUtils.isNotBlank(stateCode)) {
      this.quote().overrideShipToStateCode = stateCode;
      await firstValueFrom<EntitySaveResponse>(this.quoteService.save(this.quote()));
      await this.runMultiplier();
    }
  }

  private async runMultiplier(): Promise<void> {
    const saveResponse: EntitySaveResponse = await firstValueFrom<EntitySaveResponse>(this.quoteService.runMultiplier(this.quote()));
    this.quote.set(saveResponse.updated);
    this.updateShippingChargeFlags();
    this.saveMessages.set(saveResponse.messages);
    this.loadUpdateUser();
  }

  public openConfigurator(): void {
    this.router.navigate(['configurator', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  showConfigurator(): boolean {
    return !LeadTimeUtils.isStockProgram(this.quote().leadTimeCode);
  }

  isStockProgram(): boolean {
    return LeadTimeUtils.isStockProgram(this.quote().leadTimeCode);
  }

  leadTimeDescription(): string {
    return this.leadTimes()?.find((leadTime) => leadTime.code === this.quote().leadTimeCode)?.description ?? this.quote().leadTimeCode;
  }

  productColumn(line: EntityLineHeader): string {
    return EntityUtils.isStockUnitLine(this.quote(), line) && line.itemNumber ? line.itemNumber : line.product;
  }

  isIncompleteLine(line: EntityLineHeader): boolean {
    return EntityUtils.isIncompleteLine(this.quote(), line);
  }

  isSingleStockUnitSelected(): boolean {
    return this.selectedLines().length === 1 && EntityUtils.isStockUnitLine(this.quote(), this.selectedLines()[0]);
  }

  showStockLineSpecifications(): void {
    if (this.isSingleStockUnitSelected()) {
      this.dialogService.showStockLineSpecificationsDialog(this.selectedLines()[0]);
    }
  }

  private showRunMultiplierSnackBar(): void {
    if (!this.quote().frozen && this.quote().updateAllowed) {
      const snackBarRef = this.snackBar.open('Please run the multipler to see updated pricing', 'Run Now', {
        duration: 15000,
      });
      snackBarRef.onAction().subscribe(() => this.multiplier());
      snackBarRef.afterDismissed().subscribe((dismissed) => {
        if (!dismissed.dismissedByAction) {
          setTimeout(() => (this.saveMessages.set(new Array('Please run the multipler to see updated pricing'))), 0);
        }
      });
    }
  }

  public addRisers(): void {
    const riserDialog = this.dialog.open(RiserDialogComponent, {
      disableClose: true,
      width: '1200px',
      maxWidth: '90vw',
      height: '600px',
      data: { entity: this.quote() },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessages.set([]);
        this.quote.set(result);
        this.updateShippingChargeFlags();
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public openLeadTimeDialog(): void {
    const leadTimeDialog = this.dialog.open(LeadTimeDialogComponent, {
      disableClose: true,
      data: { leadTimes: this.leadTimes(), entityKey: this.quote().id },
    });
    leadTimeDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessages.set([]);
        this.showRunMultiplierSnackBar();
        this.quote().lines = result;
      }
    });
  }

  public showLineSummary(): void {
    if (this.selectedLines() && this.selectedLines().length === 1) {
      const selectedLine = this.selectedLines()[0];
      const lineSummaryDialog = this.dialog.open(LineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)) },
      });
      lineSummaryDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.isBusy.set(true);
          this.quote().lines.filter((line) => line.lineId == result.lineId)[0].quantity = result.quantity;
          this.isBusy.set(false);
        }
      });
    }
  }

  public warrantyLineSummaryActive(): boolean {
    return this.quote().leadTimeCode == 'WAR' || this.quote().leadTimeCode == 'CPW' || this.quote().leadTimeCode == 'RPW';
  }

  public showWarrantyLineSummary(): void {
    if (this.selectedLines() && this.selectedLines().length === 1 && this.warrantyLineSummaryActive()) {
      const selectedLine = this.selectedLines()[0];
      const lineWarrantySummaryDialog = this.dialog.open(WarrantyLineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)), dealerAddressId: this.quote().dealerAddressId },
      });
      lineWarrantySummaryDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.isBusy.set(true);
          this.quote().lines.filter((line) => line.lineId == result.lineId)[0].quantity = result.quantity;
          this.isBusy.set(false);
        }
      });
    }
  }

  public selectQuoteLineRow(clickEvent: MouseEvent, line: EntityLineHeader): void {
    if (clickEvent.button != 0) {
      return;
    }
    if (!clickEvent.ctrlKey && !clickEvent.shiftKey) {
      this.selectedLines.set([]);
      this.selectedLines.update(current => [...current, line]);
      this.selectionStartIndex = this.quote().lines.indexOf(line);
    } else {
      if (clickEvent.ctrlKey) {
        this.toggleLineSelection(line);
        this.selectionStartIndex = this.quote().lines.indexOf(line);
      }
      if (clickEvent.shiftKey) {
        this.selectedLines.set([]);
        const clickedIndex = this.quote().lines.indexOf(line);
        const first = Math.min(clickedIndex, this.selectionStartIndex);
        const last = Math.max(clickedIndex, this.selectionStartIndex);
        for (let index = first; index <= last; index++) {
          this.selectedLines.update(current => [...current, this.quote().lines[index]]);
        }
      }
    }
  }

  private toggleLineSelection(line: EntityLineHeader): void {
    const index = this.selectedLines().indexOf(line, 0);
    if (index > -1) {
      this.selectedLines.update(current => { const next = [...current]; next.splice(index, 1); return next; });
    } else {
      this.selectedLines.update(current => [...current, line]);
    }
  }

  public isLineSelected(line: EntityLineHeader): boolean {
    return this.selectedLines().indexOf(line, 0) > -1;
  }

  public getSelectedQuoteLineCount(): number {
    return this.selectedLines().length;
  }

  isDiscontinued(): boolean {
    if (!this.selectedLines() || this.selectedLines().length === 0) {
      return false;
    }
    return this.selectedLines().filter((line) => EntityLineUtils.hasDiscontinuedProduct(line)).length > 0;
  }

  public deleteSelectedLines(): void {
    this.resetStatus();
    const remainingLines = this.quote().lines.filter((quoteLine) => !this.isLineSelected(quoteLine));
    this.entityLineService.deleteLines(this.selectedLines()).subscribe({
      next: () => {
        this.quote().lines = remainingLines;
        this.selectedLines.set([]);
        this.isBusy.set(false);
        this.showRunMultiplierSnackBar();
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  public addParts(): void {
    this.dialogService.showPartsDialog(this.quote()).subscribe({
      next: (result) => {
        if (result) {
          this.quote.set(result);
          this.updateShippingChargeFlags();
          this.showRunMultiplierSnackBar();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  public copyLines(): void {
    const copyLinesDialog = this.dialog.open(CopyLinesDialogComponent, {
      disableClose: true,
      width: '1360px',
      height: '640px',
      data: { lines: JSON.parse(JSON.stringify(this.selectedLines())) },
    });
    copyLinesDialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result.id.entityId == this.quote().id.entityId) {
          this.quote.set(result);
          this.updateShippingChargeFlags();
          this.showRunMultiplierSnackBar();
        } else {
          this.isBusy.set(true);
          this.router.navigate(['quotes', result.id.entityId, result.id.entityRevision]);
        }
      }
    });
  }

  public selectAddress(): void {
    this.quoteService.save(this.quote()).subscribe({
      next: (result: EntitySaveResponse) => {
        this.quote.set(result.updated);
        this.updateShippingChargeFlags();
        this.saveMessages.set(result.messages);
        this.isBusy.set(false);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
    this.showAddressDialog();
  }

  createShippingChargeTask(): void {
    this.dialogService.showCreateShippingChargeTaskDialog(this.quote()).subscribe({
      next: (response: string) => {
        if (StringUtils.isNotBlank(response)) {
          this.isBusy.set(true);
          this.entityService.getEntityByKey(this.quote().id).subscribe({
            next: (result: Entity) => {
              this.quote.set(result);
              this.updateShippingChargeFlags();
              this.isBusy.set(false);
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
          });
        }
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  private showAddressDialog(): void {
    const addressDialog = this.dialog.open(AddressSelectionDialogComponent, {
      data: {
        entity: JSON.parse(JSON.stringify(this.quote())),
        personLogin: this.personLogin,
        readOnly: this.quote().frozen,
      },
    });
    addressDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
        this.updateShippingChargeFlags();
      }
    });
  }

  public selectSkyportContact(): void {
    this.entityService.hasSkyportCharacteristic(this.quote().id).subscribe((result) => {
      if (result && result == 'true') {
        this.quoteService.save(this.quote()).subscribe({
          next: (result: EntitySaveResponse) => {
            this.quote.set(result.updated);
            this.updateShippingChargeFlags();
            this.saveMessages.set(result.messages);
            this.isBusy.set(false);
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
        this.showSkyportContactDialog();
      }
    });
  }

  private showSkyportContactDialog(): void {
    const skyportDialog = this.dialog.open(SkyportContactDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.quote())) },
      width: '550px',
      height: '380px',
    });
    skyportDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
        this.updateShippingChargeFlags();
      }
    });
  }

  public fpaManagement(): void {
    this.errorMessages.set([]);
    this.marketingDataChecks();
    if (this.errorMessages().length === 0) {
      this.resetStatus();
      this.entityService.hasMissingRiserData(this.quote().id).subscribe({
        next: (invalidRiserData: string) => {
          if (StringUtils.equalsIgnoreCase('true', invalidRiserData)) {
            this.errorMessages.update(current => [...current, 'Please upload risers via the Riser Chart button and ensure Project Defaults are completed before requesting a FPA.']);
            this.isBusy.set(false);
          } else {
            this.quoteService.save(this.quote()).subscribe({
              next: (response: EntitySaveResponse) => {
                this.quote.set(response.updated);
                this.saveMessages.set(response.messages);
                if (LeadTimeUtils.isQuickShip(this.quote().leadTimeCode)) {
                  this.errorMessages.update(current => [...current, LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE]);
                  this.isBusy.set(false);
                } else {
                  this.router.navigate(['fpa/QUOTE/' + this.quote().id.entityId + '/' + this.quote().id.entityRevision]);
                }
              },
              error: (error: HttpErrorResponse) => this.handleError(error),
            });
          }
        },
      });
    }
  }

  private entityLineCountCheck(): void {
    if ((this.quote()?.lines?.length || 0) === 0) {
      const message = 'Please add at least one line item';
      this.errorMessages.update(current => [...current, message]);
    }
  }

  private marketingDataChecks(): void {
    this.marketSegmentMissing.set(false);
    this.projectTypeMissing.set(false);
    this.specTypeMissing.set(false);
    this.applicationTypeMissing.set(false);
    if (!this.quote().marketSegmentCode || this.quote().marketSegmentCode.length === 0) {
      this.marketSegmentMissing.set(true);
      this.errorMessages.update(current => [...current, 'Please select a Market Segment']);
    }
    if (!this.quote().projectTypeCode || this.quote().projectTypeCode.length === 0) {
      this.projectTypeMissing.set(true);
      this.errorMessages.update(current => [...current, 'Please select a Project Type']);
    }
    if (!this.quote().specTypeCode || this.quote().specTypeCode.length === 0) {
      this.specTypeMissing.set(true);
      this.errorMessages.update(current => [...current, 'Please select a Spec Type']);
    }
    if (!this.quote().applicationTypeCode || this.quote().applicationTypeCode === 0) {
      this.applicationTypeMissing.set(true);
      this.errorMessages.update(current => [...current, 'Please select a Application Type']);
    }
  }

  private quickShipQuantityChecks() {
    if (
      (EntityUtils.isCommercialQuickShip(this.quote().leadTimeCode) || EntityUtils.isResidentialQuickShip(this.quote().leadTimeCode)) &&
      this.quote().unitQuantity > this.MAX_QUICK_SHIP_QTY
    ) {
      this.errorMessages.update(current => [...current, 
        `There is a ${this.MAX_QUICK_SHIP_QTY} unit quantity limit for Quick Build programs. Please decrease your unit quantity to allow for a Quick Build lead time. `,
      ]);
    }
  }

  private multipleRequestQuantityChecks() {
    if (
      EntityUtils.isMultipleRelease(this.quote().releaseTypeId) &&
      (EntityUtils.isCommercialQuickShip(this.quote().leadTimeCode) || EntityUtils.isResidentialQuickShip(this.quote().leadTimeCode))
    ) {
      this.errorMessages.update(current => [...current, 
        'Quick Build lead times are not available as multiple release. A single release is the only available option. Please contact your CXR with any questions.',
      ]);
    }

    if (EntityUtils.isMultipleRelease(this.quote().releaseTypeId) && this.quote().unitQuantity < this.MR_MINIMUM_QTY) {
      this.errorMessages.update(current => [...current, 
        `The total unit quantity of this quote is less than ${this.MR_MINIMUM_QTY}. Multiple release requires a minimum unit quantity of at least ${this.MR_MINIMUM_QTY} units to select a multiple release. A single release is the only available option. Please contact your CXR with any questions.`,
      ]);
    }

    if (EntityUtils.isMultipleRelease(this.quote().releaseTypeId) && LeadTimeUtils.isPartsOrWarrantyLeadTime(this.quote().leadTimeCode)) {
      this.errorMessages.update(current => [...current, 
        'Lead times of Replacement Parts and Warranty are not available with multiple release orders. A single release is the only available option. Please contact your CXR with any questions.',
      ]);
    }
  }

  private async requestedShipDateChecks(): Promise<void> {
    this.isRequestedShipDateValid = true;
    const leadTime: IecLeadTime = await firstValueFrom<IecLeadTime>(this.leadTimeService.getLeadTimeByProgram(this.quote().leadTimeCode));
    const currentDateWithLeadTime = new Date(new Date().setDate(new Date().getDate() + leadTime.leadTimeDays));
    if (DateUtils.formatDisplay(new Date(this.quote().requestedShipDate)) < new Date(currentDateWithLeadTime.toLocaleDateString())) {
      this.isRequestedShipDateValid = false;
      this.errorMessages.update(current => [...current, 'Invalid Requested Ship Date.  Earliest possible requested ship date is: ' + currentDateWithLeadTime.toISOString().split('T')[0]]);
    }
  }

  private async salesRepCheck(): Promise<void> {
    if (this.quote().salesRepId && this.quote().salesRepId.length > 0) {
      try {
        const sellingOrg: Organization = this.selectedSellingOrg() ?? (await this.getOrganizationOrNull(this.quote().repOfficeId));
        if (!sellingOrg?.internal) {
          const person: Person = await firstValueFrom<Person>(this.personService.getContactPerson(this.quote().salesRepId));
          const primaryOrg: Organization = await this.getOrganizationOrNull(person.primaryOrgUnitId);
          if (primaryOrg != null && primaryOrg.internal) {
            this.errorMessages.update(current => [...current, 'The sales rep on this quote is an internal user. To place as order please select a valid sales rep from the Rep Office.']);
          }
        }
      } catch (error) {
        this.errorMessages.update(current => [...current, 'Unable to complete salesRepCheck: ' + error]);
      }
    } else {
      this.errorMessages.update(current => [...current, 'Can not place order due to missing sales rep id']);
    }
  }

  private async getOrganizationOrNull(orgUnitId: string): Promise<Organization> {
    if (!orgUnitId || orgUnitId.length === 0) {
      return null!;
    }
    try {
      return await firstValueFrom<Organization>(this.organizationService.getOrganization(orgUnitId));
    } catch {
      return null!;
    }
  }

  public viewOrder(id: EntityKey): void {
    this.router.navigate(['orders/' + id.entityId + '/' + id.entityRevision]);
  }

  public viewDocuments(): void {
    this.router.navigate(['documents/' + this.quote().id.entityName + '/' + this.quote().id.entityId + '/' + this.quote().id.entityRevision]);
  }

  private canPlaceOrCancelOrder(): boolean {
    return !this.isNew() && this.quote().updateAllowed && !EntityUtils.isApprovedOrFrozen(this.quote()) && !this.isShipToStateNeeded() && !this.isShippingChargeTaskNeeded();
  }

  public showPlaceOrderButton(): boolean {
    return this.canPlaceOrCancelOrder() && !this.hasOpenCreditDocumentsTask();
  }

  public showCancelPlaceOrderButton(): boolean {
    return this.canPlaceOrCancelOrder() && this.hasOpenCreditDocumentsTask();
  }

  public cancelPlaceOrder(): void {
    this.resetStatus();
    this.isBusy.set(true);
    this.quoteService.cancelPlaceOrder(this.quote().id).subscribe({
      next: (result: EntitySaveResponse) => {
        this.quote.set(result.updated);
        this.isBusy.set(false);
        this.saveMessages.set(result.messages);
        this.checkForOpenCreditDocumentsTask();
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  public placeOrder(): void {
    const salesChannelMessages: string[] = this.salesChannelPlaceOrderMessages();
    if (salesChannelMessages.length > 0) {
      this.dialogService.showGenericMessageDialog(salesChannelMessages);
    } else if (!this.quote().associatedOrderId && this.quote().leadTimeCode == 'MOK') {
      this.promptForParentEntity();
    } else {
      this.dataChecks().then(() => {
        if (this.errorMessages().length == 0 && this.isRequestedShipDateValid) {
          this.resetStatus();
          this.quoteService.placeOrder(this.quote()).subscribe({
            next: (result: EntitySaveResponse) => {
              this.quote.set(result.updated);
              this.updateShippingChargeFlags();
              this.isBusy.set(false);
              this.isNew.set(false);
              this.saveMessages.set(result.messages);
              this.loadUpdateUser();
            },
            error: (error) => {
              this.handleError(error);
              this.checkForOpenCreditDocumentsTask();
            },
          });
        }
      });
    }
  }

  private salesChannelPlaceOrderMessages(): string[] {
    const messages: string[] = [];
    const customerIsRep = this.quote().customerId === this.quote().repOfficeId;
    if (this.quote().salesChannelCode === 'B' && !customerIsRep) {
      messages.push('This quote cannot be placed as the customer is different than the rep and it is marked as buy resell. Please update your quote accordingly.');
    }
    if (this.quote().salesChannelCode === 'C' && customerIsRep) {
      messages.push('This quote cannot be placed as the customer is the same as the rep and it is marked as commission Please update your quote accordingly.');
    }
    if (this.quote().salesChannelCode === 'C' && this.quote().multiplier === 1) {
      messages.push('This quote cannot be placed as it is a commission quote with a standard multiplier of 1.00. Please update your quote accordingly.');
    }
    return messages;
  }

  private async dataChecks(): Promise<void> {
    this.errorMessages.set([]);
    this.entityLineCountCheck();
    this.marketingDataChecks();
    this.quickShipQuantityChecks();
    this.multipleRequestQuantityChecks();
    await this.requestedShipDateChecks();
    await this.salesRepCheck();
  }

  public specialQuotes(): void {
    if (!['COM', 'CSP'].includes(this.quote().leadTimeCode)) {
      this.errorMessages.update(current => [...current, 'Special Quotes are only available with a Standard Lead Time or Standard Replacement Parts. To add a Special Feature you will need to change the lead time to Standard Lead Time or Standard Replacement Parts']);
    } else {
      this.dialogService.showSpecialQuotesDialog(this.quote().id, this.personLogin, this.quote().brandID).subscribe({
        next: () => {
          this.entityService.getEntityByKey(this.quote().id).subscribe({
            next: (result: Entity) => {
              this.quote.set(result);
            },
            error: (error: HttpErrorResponse) => this.handleError(error),
          });
        },
        error: (error) => {
          this.handleError(error);
        },
      });
    }
  }

  public viewEntityBreaks(): void {
    const breaksDialog = this.dialog.open(EntityBreakDialogComponent, {
      data: { entityKey: this.quote().id, personLogin: this.personLogin, brandID: this.quote().brandID },
    });
    breaksDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public cancel(): void {
    const cancelDialog = this.dialog.open(EntityCancelDialogComponent, {
      data: { entityKey: this.quote().id },
    });
    cancelDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
        this.updateShippingChargeFlags();
        this.loadUpdateUser();
      }
    });
  }

  public showSalesRep(): void {
    if (this.quote().salesRepId && this.quote().salesRepId.length > 0) {
      this.personService.getContactPerson(this.quote().salesRepId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showIecContact(): void {
    if (this.quote().iecContactId && this.quote().iecContactId.length > 0) {
      this.personService.getContactPerson(this.quote().iecContactId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showWarrantyTextLink(): boolean {
    return !!(
      (this.quote().leadTimeCode == 'WAR' || this.quote().leadTimeCode == 'CPW' || this.quote().leadTimeCode == 'RPW') &&
      this.quote().warrantyRequestNotes &&
      this.quote().warrantyRequestNotes.length > 0
    );
  }

  public showWarrantyText(): void {
    this.dialog.open(WarrantyTextDialogComponent, {
      disableClose: true,
      width: '400px',
      height: '250px',
      data: { warrantyText: this.quote().warrantyRequestNotes },
    });
  }

  public fixPermissions(): void {
    this.resetStatus();
    this.entityService.fixPermissions(this.quote().id).subscribe({
      next: () => (this.isBusy.set(false)),
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  canApprove(): boolean {
    switch (this.quote().approvalStatus) {
      case 'Admin Review':
        return this.personLoginService.hasRole(RoleName.IT);
      case 'Credit Review':
        return this.personLoginService.hasRole(RoleName.IT) || this.personLoginService.hasRole(RoleName.CredAnalyst);
      case 'CSR Review':
      case 'Pending Approval':
        return this.personLoginService.hasRole(RoleName.IT) || this.personLoginService.hasRole(RoleName.CustomerService);
      default:
        return false;
    }
  }

  approveQuote(): void {
    this.resetStatus();
    this.quoteService.approveQuote(this.quote().id).subscribe({
      next: (result: EntitySaveResponse) => {
        this.quote.set(result.updated);
        this.updateShippingChargeFlags();
        this.isBusy.set(false);
        this.isNew.set(false);
        this.loadUpdateUser();
        this.saveMessages.set(result.messages);
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  openUnitTaggingScreen(): void {
    this.router.navigate(['unitTagging', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  getIncludeShippingTagsTooltip(): string {
    const frozenOrApproved: boolean = this.quote().frozen || this.quote().approvalStatus.indexOf('Approved') >= 0;
    if (frozenOrApproved) {
      if (this.quote().includeShippingTags) {
        return 'Click the Tagging button to supply shipping tag information';
      } else {
        return 'You will need to generate a revision to add shipping tags';
      }
    } else {
      return 'Click the Tagging button to set this value and supply shipping tag information';
    }
  }

  openProjectDefaultsScreen(): void {
    this.router.navigate(['projectDefaults', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  openRiserChartDialog(): void {
    const riserDialog = this.dialog.open(RiserChartDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entityKey: this.quote().id },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.entityService.getEntityByKey(this.quote().id).subscribe({
          next: (result: Entity) => {
            this.quote.set(result);
            this.updateShippingChargeFlags();
            this.isBusy.set(false);
          },
          error: (error: HttpErrorResponse) => this.handleError(error),
        });
      }
    });
  }

  canAddLines(): boolean {
    return this.quote() && !this.quote().frozen && this.quote().approvalStatus != 'Approved' && !this.isStockProgram();
  }

  canAddRisers(): boolean {
    return this.canAddLines() && BrandID.CLM !== this.quote()?.brandID;
  }

  canUpdateExtendedWarranty(): boolean {
    return ExtendedWarrantyUtils.canWarrantyBeChanged(this.quote());
  }

  showExtendedWarrantyDialog(): void {
    this.dialogService.showExtendedWarrantyDialog(this.quote()).subscribe({
      next: (result: boolean) => {
        if (result) {
          this.isBusy.set(true);
          this.entityService.getEntityByKey(this.quote().id).subscribe({
            next: (entity) => {
              this.quote.set(entity);
              this.updateShippingChargeFlags();
              this.isBusy.set(false);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  openQewlDialog(): void {
    this.dialog.open(QewlDialogComponent, {
      width: '1360px',
      height: '650px',
      data: this.quote(),
    });
  }

  promptForParentEntity(): void {
    const selectParentEntityDialong = this.dialog.open(SelectParentEntityDialogComponent, {
      width: '1200px',
      height: '700px',
      data: this.quote(),
      disableClose: true,
    });

    selectParentEntityDialong.afterClosed().subscribe({
      next: (parentKey) => {
        if (parentKey !== null && parentKey.length !== 0) {
          this.quote().parentId = parentKey;
          this.errorMessages.set([]);
          this.marketingDataChecks();
          if (this.errorMessages().length == 0) {
            this.resetStatus();
            this.quoteService.placeOrder(this.quote()).subscribe({
              next: (result) => {
                this.quote.set(result.updated);
                this.updateShippingChargeFlags();
                this.isBusy.set(false);
                this.isNew.set(false);
                this.saveMessages.set(result.messages);
                this.loadUpdateUser();
              },
              error: (error) => {
                this.handleError(error);
                this.checkForOpenCreditDocumentsTask();
              },
            });
          }
        } else {
          alert('A parent entity must be selected before placing this order.');
        }
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  public showSpecialQuotes(): boolean {
    return (
      !this.isNew() &&
      !LeadTimeUtils.isStockProgram(this.quote().leadTimeCode) &&
      this.quote().updateAllowed &&
      this.quote().projectProductType != ProjectProductType.RES &&
      !!this.quote().lines &&
      this.quote().lines.length > 0
    );
  }

  createErrorMessage(): string {
    let errorMessage = '';
    for (const message of this.errorMessages()) {
      errorMessage = errorMessage + message + '\n';
    }

    return errorMessage;
  }

  openCostReport(): void {
    this.router.navigate(['/', 'entityCostReport', this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  isSureFlowUnit(line: EntityLineHeader): boolean {
    const productId = line?.product || '';
    return productId.toUpperCase().indexOf('SUREFLOW') >= 0;
  }

  isSureFlowUnitSelected(): boolean {
    if (!this.selectedLines() || this.selectedLines().length === 0) {
      return false;
    }
    return this.selectedLines().filter((line) => this.isSureFlowUnit(line)).length > 0;
  }

  isAssignNewOpportunityOptionDisplayed(): void {
    if (this.isInternalOrRsm()) {
      this.opportunityService.getAssociatedQuotes(this.quote().opportunityId, this.quote().opportunityRevision).subscribe({
        next: (result) => {
          this.showAssignNewOpportunityOption.set(result.length > 1);
        },
        error: () => {
          this.showAssignNewOpportunityOption.set(false);
        },
      });
    }
  }

  assignNewOpportunity(): void {
    this.resetStatus();
    this.quoteService.assignNewOpportunity(this.quote()).subscribe({
      next: (result) => {
        this.quote.set(result.updated);
        this.updateShippingChargeFlags();
        this.saveMessages.set(result.messages);
        const snackBarMessage = 'New Opportunity ID: ' + this.quote().opportunityId + ';' + this.quote().opportunityRevision;
        this.snackBar.open(snackBarMessage, 'Close');
        this.loadUpdateUser();
        this.showAssignNewOpportunityOption.set(false);
      },
      error: (error) => {
        this.handleError(error);
      },
      complete: () => {
        this.isBusy.set(false);
      },
    });
  }

  updateOpportunityIfRequired() {
    let daysSinceUpdated = 0;
    this.opportunityService.getOpportunity(this.quote().opportunityId, this.quote().opportunityRevision).subscribe({
      next: (result) => {
        this.associatedOpportunity.set(result);
        daysSinceUpdated = (new Date().getTime() - new Date(this.associatedOpportunity().updateTimestamp).getTime()) / (1000 * 3600 * 24);
        const closed = ['CLS', 'JWON', 'JLST'].includes(result.currentPhaseCode) || ['CLS'].includes(result.statusCode);
        if (!closed && daysSinceUpdated > 90 && this.quote().totalList > 50000) {
          this.openRequiredOpportunityUpdatesDialog();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  public openRequiredOpportunityUpdatesDialog(): void {
    this.dialog.open(RequireOpportunityUpdatesDialogComponent, {
      disableClose: true,
      width: '500px',
      height: '400px',
      data: { opportunity: this.associatedOpportunity() },
    });
  }

  public enterContact(isPrimaryContact: boolean): void {
    const dialog = this.dialog.open(EnterContactDialogComponent, {
      data: { isPrimaryContact: isPrimaryContact, entity: this.quote() },
    });
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result instanceof HttpErrorResponse) {
          this.handleError(result);
        } else {
          this.quote.set(result);
          this.updateShippingChargeFlags();
        }
      }
    });
  }

  loadSalesReps(brandID: BrandID): void {
    this.selectionService.getOrganizationPeople(this.quote().repOfficeId, brandID).subscribe({
      next: (results) => {
        this.salesReps.set(results);
        this.outsideSalesReps.set(results);
      },
    });
  }

  brandChange(): void {
    const split: string[] = this.selectedBrand().split('-');
    this.quote().brandID = BrandID[split[0] as keyof typeof BrandID];
    this.personLoginService.setSelectedBrand(this.quote().brandID);
    this.quote().projectProductType = ProjectProductType[split[1] as keyof typeof ProjectProductType];
    this.loadLeadTimes(this.quote().brandID, this.quote().projectProductType);
    this.loadSalesReps(this.quote().brandID);
  }

  private updateFpaStatusFlags(): void {
    this.canCancelFpaReview.set(false);
    this.canSubmitFpa.set(false);
    if (StringUtils.isNotBlank(this.quote()?.id?.entityId)) {
      const fpaStatus: string = this.quote()?.fpaStatus || 'None';
      this.fpaService.isPendingApproval(fpaStatus).subscribe({
        next: (response: string) => {
          this.canCancelFpaReview.set(StringUtils.equalsIgnoreCase('true', response));
          this.canSubmitFpa.set(!this.quote().frozen && !this.canCancelFpaReview());
        },
        error: (error: HttpErrorResponse) => this.handleError(error),
      });
    }
  }

  showFpaManagementButton(): boolean {
    return this.canSubmitFpa() && !LeadTimeUtils.isStockProgram(this.quote().leadTimeCode);
  }

  showFpaCancelButton(): boolean {
    return this.canCancelFpaReview();
  }

  cancelFpaReview(): void {
    this.isBusy.set(true);
    this.fpaService.cancelSubmission(EntityKeyUtils.convertToFullEntityKey(this.quote().id)).subscribe({
      next: () => {
        this.entityService.getEntityByKey(this.quote().id).subscribe({
          next: (result: Entity) => {
            this.quote.set(result);
            this.updateShippingChargeFlags();
            this.updateFpaStatusFlags();
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error: HttpErrorResponse) => this.handleError(error),
    });
  }

  shipViaChange(shipViaValue: string) {
    if (shipViaValue.includes('CPU') || shipViaValue.includes('SCA') || shipViaValue.includes('3PB')) {
      this.quote().shipAndHandChargeType = 'CPU';
    } else {
      this.quote().shipAndHandChargeType = 'PPA';
    }
  }

  isValidWarrantyLeadTime(): boolean {
    if (this.quote()) {
      return LeadTimeUtils.validWarrantyLeadTime(this.quote().leadTimeCode, this.quote().brandID);
    }
    return false;
  }

  showEntityHistoryDialog(): void {
    if (this.quote()) {
      this.dialogService.showEntityHistoryDialog(this.quote());
    }
  }

  showUnrestrictedEditOption(): boolean {
    return this.quote() && !this.isNew() && this.quote().updateAllowed && this.quote().frozen && this.canUpdateInternalNotes();
  }

  canUpdateInternalNotes(): boolean {
    return this.userHasRole("CustomerService") || this.userHasRole("IT") || this.userHasRole("CredAnalyst") || this.userHasRole("ApplicationEngineer") || this.userHasRole("RSM");
  }

  showUnrestrictedChangesDialog(): void {
    if (this.showUnrestrictedEditOption()) {
      this.dialogService.showUnrestrictedEntityUpdatesDialog(this.quote()).subscribe({
        next: (update: UnrestrictedEntityUpdate) => {
          if (update) {
            this.isBusy.set(true);
            this.errorMessages.set([]);
            this.entityService.saveEntityWithoutRestrictions(update).subscribe({
              next: (updatedEntity: Entity) => {
                this.quote.set(updatedEntity);
                this.isBusy.set(false);
              }, error: (error: any) => this.handleError(error)
            });
          }
        }, error: (error: any) => this.handleError(error)
      });
    }
  }

  protected readonly LeadTimeUtils = LeadTimeUtils;

  private initializeFromRouteParams(): void {
    const idString: string = this.quoteId();
    const revisionString: string = this.quoteRevision();
    if (idString && idString.length > 0 && revisionString) {
      if ('new' === idString) {
        this.startNewQuote(this.orgUnitId());
      } else {
        const key: EntityKey = new EntityKey();
        key.entityId = idString;
        key.entityName = 'QUOTE';
        key.entityRevision = 0;
        this.quoteService.isRepriceNeeded(key).subscribe({
          next: (result) => {
            if (result && result == 'true') {
              this.waitMessage.set('Updating List Prices...');
              this.quoteService.reprice(key).subscribe({
                next: (result) => {
                  this.loadQuoteDetails(result);
                },
                error: (error) => this.handleError(error),
              });
            } else {
              this.entityService.getEntity('QUOTE', idString, +revisionString).subscribe({
                next: (result) => {
                  this.loadQuoteDetails(result);
                },
                error: (error) => this.handleError(error),
              });
            }
          },
          error: (error) => this.handleError(error),
        });
      }
    } else {
      this.backToQuoteMaster();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}
